import { createSetupIntent, updateBookingStripePaymentMethod, setPaymentIntentUseForAllPayments } from 'actions/stripe';
import { createBillectaInvoice } from 'actions/billecta';
import paymentProviders from 'enums/paymentProviders';
import purchaseItemTypes from 'enums/purchaseItemTypes';
import routes from 'routes';
import strings from 'localization/strings';
import { handleResponse } from 'actions/actionHelpers';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { createRoundingCorrectionPurchaseItem } from 'helpers/PurchaseItemHelper';
import { toAddressKeyValueObject, toDataContractAddress, getAddressOrDefault } from 'helpers/AddressHelper';
import { toCamelCase } from 'helpers/StringHelper';
import { removeQuerystringParameters } from 'helpers/BrowserHelper';
import { fetchPaymentIntent } from 'actions/stripe';
import queryString from 'query-string';
import { createGoogleTagManagerUserAction } from 'integration/google-tag-manager/googleTagManagerHelper';
import { updateTenantBooking } from 'actions/tenantBookings';
import { roundAmount, calculateVat } from 'helpers/MonetaryHelper';
import { calculateSum } from 'helpers/ArrayHelper';

export const makePayment = ({ payment, dispatch, type, booking, purchase, successUrlParameters, onFailure }) => {
    switch(payment.paymentProvider) {
        case paymentProviders.stripe.key:
            makeStripePayment({
                stripe: payment.stripe,
                type,
                booking,
                purchase,
                successUrlParameters,
                onFailure,
                dispatch
            });
            break;

        case paymentProviders.billecta.key:
            makeBillectaPayment({
                billecta: payment.billecta,
                dispatch,
                type,
                booking,
                purchase,
                successUrlParameters,
                onFailure
            });
            break;

        default:
            break;
    }
};

const makeStripePayment = ({ stripe, type, booking, purchase, successUrlParameters, onFailure, dispatch }) => {
    const sendApiRequest = () => confirmStripePayment({ stripe, type, booking, purchase, successUrlParameters, onFailure });

    if(stripe.useForAllPayments) {
        dispatch(setPaymentIntentUseForAllPayments(stripe.paymentContext.paymentIntentId))
            .then(handleResponse(
                () => {
                    sendApiRequest();
                },
                response => {
                    onFailure(response);
                }
            ));
    } else {
        sendApiRequest();
    }
};

const confirmStripePayment = ({ stripe, type, booking, purchase, successUrlParameters, onFailure }) => {
    const reference = {
        type,
        bookingId: booking.id,
        immediateBookingRequestId: booking.immediateBookingRequestId,
        purchaseId: purchase?.id
    };

    // https://stripe.com/docs/js/payment_intents/confirm_payment
    const options = {
        confirmParams: {
            return_url: createPaymentSuccessUrl(booking, purchase, successUrlParameters, paymentProviders.stripe.key)
        }
    };

    if(stripe.paymentMethodId === 'other') {
        options.elements = stripe.paymentContext.elements;
    } else {
        options.clientSecret = stripe.paymentContext.clientSecret;
        options.confirmParams.payment_method = stripe.paymentMethodId;
    }

    stripe.paymentContext.stripe.confirmPayment(options)
        .then(response => {
            logErrorToApplicationInsights(response, reference);
            onFailure(response);
        }); // the promise only resolves on failure
};

const makeBillectaPayment = ({ billecta, dispatch, type, booking, purchase, successUrlParameters, onFailure }) => {
    const request = {
        type,
        bookingId: booking.id,
        purchaseId: purchase?.id,
        address: billecta.address
    };
    dispatch(createBillectaInvoice(request))
        .then(handleResponse(
            response => {
                const createdPurchase = response.payload.purchase;
                window.location.href = createPaymentSuccessUrl(booking, createdPurchase, successUrlParameters, paymentProviders.billecta.key);
            },
            response => {
                onFailure(response);
            }
        ));
};

export const updateBookingPaymentSetup = ({ setup, dispatch, booking, successUrlParameters, onFailure }) => {
    const updateBookingRequest = {
        bookingStatus: booking.bookingStatus,
        storageId: booking.storageId,
        goodsType: booking.goodsType,
        registrationNumber: booking.registrationNumber,
        paymentProvider: setup.paymentProvider,
        invoiceAddress: setup.paymentProvider === paymentProviders.billecta.key
            ? setup.billecta.address
            : undefined
    };

    // first update the booking with the new payment provider
    dispatch(updateTenantBooking(booking.id, updateBookingRequest))
        .then(handleResponse(
            () => {
                switch(setup.paymentProvider) {
                    case paymentProviders.stripe.key:
                        // save the Stripe payment method details and redirect to success page
                        if(setup.stripe.paymentMethodId === 'other') {
                            // a new payment method - use setup intent mechanism
                            updateStripePaymentSetup({
                                setupContext: setup.stripe.setupContext,
                                dispatch,
                                createSetupIntentParameters: {
                                    bookingId: booking.id,
                                    useForAllPayments: setup.stripe.useForAllPayments
                                },
                                successUrl: createBookingSetupSuccessUrl(booking, successUrlParameters, paymentProviders.stripe.key),
                                onFailure
                            });
                        } else {
                            // an existing payment method - plain api call
                            updateStripePaymentMethod({
                                bookingId: booking.id,
                                paymentMethodId: setup.stripe.paymentMethodId,
                                useForAllPayments: setup.stripe.useForAllPayments,
                                onSuccess: () => {
                                    window.location.href = createBookingSetupSuccessUrl(booking, successUrlParameters, paymentProviders.stripe.key);
                                },
                                onFailure,
                                dispatch
                            });
                        }
                        break;
                    case paymentProviders.billecta.key:
                        // redirect to success page
                        window.location.href = createBookingSetupSuccessUrl(booking, successUrlParameters, paymentProviders.billecta.key);
                        break;

                    default:
                        break;
                }
            },
            response => onFailure(response)
        ));
};

export const updateActorPaymentSetup = ({ setup, dispatch, actor, onFailure }) => {
    updateStripePaymentSetup({
        setupContext: setup.stripe.setupContext,
        dispatch,
        createSetupIntentParameters: { actorId: actor.id },
        successUrl: createActorSetupSuccessUrl(),
        onFailure
    });
};

const updateStripePaymentSetup = ({ setupContext, dispatch, createSetupIntentParameters, successUrl, onFailure }) => {
    // first, we have to submit the Stripe elements
    setupContext.elements.submit()
        .then(response1 => {
            if(response1.error) {
                onFailure(response1);
            } else {
                // then, create the setup intent
                dispatch(createSetupIntent(createSetupIntentParameters))
                    .then(handleResponse(
                        response2 => {
                            const setupIntent = response2.payload;
                            // finally, call confirmSetup, which will redirect to success page when completed
                            // https://stripe.com/docs/js/setup_intents/confirm_setup
                            setupContext.stripe.confirmSetup({
                                elements: setupContext.elements,
                                clientSecret: setupIntent.clientSecret,
                                confirmParams: {
                                    return_url: successUrl
                                }
                            })
                            .then(response3 => {
                                onFailure(response3);
                            }); // the promise only resolves on failure
                        },
                        response2 => {
                            onFailure(response2);
                        }
                    ));
            }
        });
};

const updateStripePaymentMethod = ({ bookingId, paymentMethodId, useForAllPayments, onSuccess, onFailure, dispatch }) => {
    const request = {
        paymentMethodId,
        useForAllPayments
    };

    dispatch(updateBookingStripePaymentMethod(bookingId, request))
        .then(handleResponse(
            () => {
                onSuccess();
            },
            response => {
                onFailure(response);
            }
        ));
};

export const sanitizePaymentFormValues = (values, stripePaymentContext) => {
    if(!values.payment) {
        return values;
    }

    const sanitizedPayment = {
        ...values.payment,
        stripe: values.payment.paymentProvider === paymentProviders.stripe.key
            ? {
                ...values.payment.stripe,
                paymentContext: stripePaymentContext
            }
            : undefined,
        billecta: values.payment.paymentProvider === paymentProviders.billecta.key
            ? {
                ...values.payment.billecta,
                address: values.payment.billecta.collectAddress
                    ? toDataContractAddress(values.payment.billecta.address)
                    : undefined
            }
            : undefined
    };

    return {
        ...values,
        payment: sanitizedPayment
    };
};

export const sanitizeSetupFormValues = (values, stripeSetupContext) => {
    if(!values.setup) {
        return values;
    }

    return {
        ...values,
        setup: {
            ...values.setup,
            stripe: {
                ...values.setup.stripe,
                setupContext: stripeSetupContext
            },
            billecta: {
                ...values.setup.billecta,
                address: toDataContractAddress(values.setup.billecta.address)
            }
        }
    };
};

// returns an array of payment provider objects
export const getAvailablePaymentProviders = appContext => Object.values(paymentProviders)
    .filter(paymentProvider => appContext.payment.availablePaymentProviders.find(pp => toCamelCase(pp) === paymentProvider.key));

// returns an array of payment provider objects
export const getDefaultPaymentProviders = appContext => Object.values(paymentProviders)
    .filter(paymentProvider => appContext.payment.defaultPaymentProviders.find(pp => toCamelCase(pp) === paymentProvider.key));

export const createPaymentFormInitialValues = (options, appContext) => {
    const availablePaymentProviders = getAvailablePaymentProviders(appContext);
    const stripe = options.stripe ?? {};

    let paymentProviderKey = availablePaymentProviders.length === 1
        ? availablePaymentProviders[0].key
        : options.paymentProvider; // a string value

    if(paymentProviderKey && !availablePaymentProviders.find(paymentProvider => paymentProvider.key === paymentProviderKey)) {
        paymentProviderKey = availablePaymentProviders.length === 1
            ? availablePaymentProviders[0].key
            : undefined;
    }

    return {
        paymentProvider: paymentProviderKey ?? null,
        stripe: {
            ...stripe,
            useForAllPayments: false
        },
        billecta: options.billecta
            ? {
                ...options.billecta,
                address: toAddressKeyValueObject(getAddressOrDefault(options.billecta.address, appContext))
            }
            : {}
    };
};

export const createSetupFormInitialValues = createPaymentFormInitialValues;

export const logErrorToApplicationInsights = (data, reference) => {
    if(window.applicationInsights) {
        window.applicationInsights.trackTrace({
            message: JSON.stringify(data),
            properties: { type: 'Payment', reference: JSON.stringify(reference) },
            severityLevel: SeverityLevel.Error
        });
    }
};

export const handlePaymentCompleted = ({ purchase, googleTagManagerMetadata, dispatch, history }) => {
    const querystringParameters = queryString.parse(history.location.search);
    removeQuerystringParameters(history, ['paymentCompleted', 'paymentProvider', 'purchaseId', 'immediateBookingRequestId', 'payment_intent', 'payment_intent_client_secret', 'redirect_status', 'action']); // cleanup URL parameters

    if(querystringParameters.action) {
        switch(querystringParameters.paymentProvider) {
            case paymentProviders.stripe.key:
                dispatch(fetchPaymentIntent(querystringParameters.payment_intent))
                    .then(handleResponse(
                        response => {
                            const paymentIntent = response.payload;
                            dispatch(createGoogleTagManagerUserAction(querystringParameters.action, googleTagManagerMetadata, paymentIntent.amount / 100 /* TODO: smallest currency unit; now assuming SEK or EUR */, paymentIntent.currency.toUpperCase()));
                        }
                    ));
                break;

            case paymentProviders.billecta.key:
                dispatch(createGoogleTagManagerUserAction(querystringParameters.action, googleTagManagerMetadata, purchase.amount, purchase.currency));
                break;

            default:
                break;
        }
    }
};

export const getPaymentCompletedDialogContent = querystringParameters => {
    switch(querystringParameters.paymentProvider) {
        case paymentProviders.stripe.key:
            return { title: strings.paymentSucceededTitle, body: strings.paymentSucceededBody, useAnimation: true };
        case paymentProviders.billecta.key:
            return { title: strings.billectaInvoiceCreationSucceededTitle, body: strings.billectaInvoiceCreationSucceededBody, useAnimation: true };
        default:
            return {};
    }
};

export const handleSetupCompleted = ({ history }) => {
    removeQuerystringParameters(history, ['setupCompleted', 'paymentProvider', 'setup_intent', 'setup_intent_client_secret', 'redirect_status']); // cleanup URL parameters
};

export const stripeFormIsIncomplete = (item, stripePaymentContext) =>
    item.paymentProvider === paymentProviders.stripe.key &&
    item.stripe.paymentMethodId === 'other' &&
    !stripePaymentContext.paymentElementComplete;

const createPaymentSuccessUrl = (booking, purchase, parameters, paymentProvider) => {
    const url = `${window.location.protocol}//${window.location.host}${routes.account.tenantBookingDetails.replace(':bookingId', booking?.id)}`;
    const querystringParameters = {
        ...(parameters ?? {}),
        paymentCompleted: true,
        paymentProvider,
        purchaseId: purchase?.id,
        immediateBookingRequestId: booking.immediateBookingRequestId
    };
    return url + '?' + queryString.stringify(querystringParameters);
};

const createBookingSetupSuccessUrl = (booking, parameters, paymentProvider) => {
    const url = `${window.location.protocol}//${window.location.host}${routes.account.tenantBookingDetails.replace(':bookingId', booking?.id)}`;
    const querystringParameters = {
        ...(parameters ?? {}),
        setupCompleted: true,
        paymentProvider
    };
    return url + '?' + queryString.stringify(querystringParameters);
};

const createActorSetupSuccessUrl = () => {
    const querystringParameters = {
        tab: 'stripePaymentMethod',
        setupCompleted: true
    };
    return `${window.location.protocol}//${window.location.host}${routes.account.settings}?${queryString.stringify(querystringParameters)}`;
};

const getZeroFee = appContext => ({
    amount: 0,
    vat: 0,
    currency: appContext.currency.code
});

export const getInvoiceFee = (paymentProvider, appContext) => {
    let invoiceFee;

    if(!paymentProvider) {
        invoiceFee = getZeroFee(appContext);
    } else {
        const paymentProviderSettings = appContext.payment[paymentProvider];
        if(!paymentProviderSettings?.invoiceFee) {
            invoiceFee = getZeroFee(appContext);
        } else {
            invoiceFee =  {
                amount: paymentProviderSettings.invoiceFee,
                vat: calculateVat(paymentProviderSettings.invoiceFee, appContext.defaultVatRate, appContext)
            };
        }
    }
    return {
        ...invoiceFee,
        vatRate: appContext.defaultVatRate,
        amountIncludingVat: invoiceFee.amount + invoiceFee.vat
    };
};

export const getPurchaseAmountIncludingInvoiceFeeAndRoundingCorrection = (booking, purchase, paymentProvider, appContext) => {
    const purchaseItems = getPurchaseItemsIncludingInvoiceFeeAndRoundingCorrection(booking, purchase, paymentProvider, appContext);
    return calculateSum(purchaseItems, o => o.amount + o.vat);
};

export const getPurchaseItemsIncludingInvoiceFeeAndRoundingCorrection = (booking, purchase, paymentProvider, appContext) => {
    return applyRoundingCorrection(
        applyInvoiceFee(booking, purchase, purchase.paymentProvider ?? paymentProvider, appContext),
        appContext
    );
};

export const getSelectedPaymentProvider = (booking, availablePaymentProviders) => {
    let selectedPaymentProvider = availablePaymentProviders.length === 1
        ? availablePaymentProviders[0]
        : booking?.paymentProvider;

    if(selectedPaymentProvider && !availablePaymentProviders.includes(selectedPaymentProvider)) {
        selectedPaymentProvider = undefined;
    }

    return selectedPaymentProvider;
};

export const getAvailableAndCurrentPaymentProviders = booking =>
    Object.keys(paymentProviders).filter(key => booking.storageGroup.storageSite.paymentProviders.includes(key) || key === booking.paymentProvider);


const applyInvoiceFee = (booking, purchase, paymentProvider, appContext) => {
    const isFirstPurchaseInTakeover = booking.takeoverTenantId && booking.purchases[0]?.id === purchase.id;
    const skipInvoiceFee = isFirstPurchaseInTakeover;
    const invoiceFee = skipInvoiceFee
        ? getZeroFee(appContext)
        : getInvoiceFee(paymentProvider, appContext);
    const adjustedPurchaseItems = [...purchase.purchaseItems.filter(o => o.type !== purchaseItemTypes.invoiceFee.key)];

    if(invoiceFee.amount) {
        adjustedPurchaseItems.push({
            ...invoiceFee,
            commissionRate: 1,
            description: strings.invoiceFee,
            type: purchaseItemTypes.invoiceFee.key
        });
    }
    return adjustedPurchaseItems;
};

const applyRoundingCorrection = (purchaseItems, appContext) => {
    const adjustedPurchaseItems = [...purchaseItems.filter(o => o.type !== purchaseItemTypes.roundingCorrection.key)];
    const sum = calculateSum(adjustedPurchaseItems, o => o.amount + o.vat);
    const roundedAmount = roundAmount(sum, appContext, true /* useDisplayPriceRounding */);
    const roundingCorrection = roundedAmount - sum;

    if(roundingCorrection) {
        adjustedPurchaseItems.push(createRoundingCorrectionPurchaseItem(roundingCorrection, appContext));
    }
    return adjustedPurchaseItems;
};
